import { z } from "zod";
import { calculationItemInputSchema, rulesetSelectorSchema, ufSchema } from "./calculation.js";
import { LOT_MAX_SHEETS, lotFieldSchema } from "./lot-mapping.js";
/**
 * Structured findings about an uploaded workbook. Every finding says where it
 * is (sheet, row, column) and what was expected, so the console can explain it
 * in the operator's language without parsing a message.
 */
export const workbookIssueSeveritySchema = z.enum(["ERROR", "WARNING", "INFO"]);
export const workbookIssueCodeSchema = z.enum([
    // File and structure
    "FILE_TOO_LARGE",
    "INVALID_WORKBOOK",
    "ENCRYPTED_WORKBOOK",
    "TOO_MANY_SHEETS",
    "TOO_MANY_COLUMNS",
    "TOO_MANY_ROWS",
    "WORKSHEET_NOT_FOUND",
    "HEADER_ROW_OUT_OF_RANGE",
    "COLUMN_MAPPING_OUT_OF_RANGE",
    "NO_DATA_ROWS",
    "TOO_MANY_DATA_ROWS",
    "SHEET_NOT_CONVERTIBLE",
    "TEMPLATE_HEADER_MISMATCH",
    "TEMPLATE_SHEET_COUNT",
    "TEMPLATE_MAPPING_MISMATCH",
    "ROWS_SKIPPED",
    "INVALID_CSV",
    /** The archive was damaged; its parts were recovered and the data must be checked. */
    "FILE_RECOVERED",
    /** A confirmed repair does not match the cell it names (the file changed or the value is invalid). */
    "REPAIR_REJECTED",
    // Cells
    "CACHED_ERROR_VALUE",
    "FORMULA_NOT_CACHED",
    "FORMULA_REVIEW_REQUIRED",
    // Mapping
    "COLUMN_REQUIRED",
    "COLUMN_NOT_DETECTED",
    "COLUMN_LOW_CONFIDENCE",
    "COLUMN_DUPLICATE",
    // Route
    "ROUTE_REQUIRED",
    "ROUTE_COLUMN_REQUIRED",
    "ROUTE_OVERRIDDEN",
    // Values
    "MISSING_VALUE",
    "INVALID_NCM",
    /** Ten digits (3304.99.90.01): an 8-digit NCM plus an internal suffix, never truncated silently. */
    "NCM_WITH_SUFFIX",
    "INVALID_CEST",
    "INVALID_UF",
    "INVALID_MONEY",
    "AMOUNT_ROUNDED",
    "ZERO_AMOUNT",
    "INVALID_QUANTITY",
    "INVALID_RATE",
    "INVALID_EXPECTED_AMOUNT",
    "DUPLICATE_ITEM_ID",
    "INVALID_ITEM",
    /**
     * The origin cell is not a CST 0–8, not a nacional/importada/estrangeira label, and not a blank
     * or N/A. The row is still calculated; the origin is left unset.
     */
    "GOODS_ORIGIN_UNREADABLE",
]);
/** Excel error literals that can be cached in a formula cell. */
export const workbookCellErrorSchema = z.enum([
    "#REF!",
    "#VALUE!",
    "#N/A",
    "#DIV/0!",
    "#NAME?",
    "#NUM!",
    "#NULL!",
    "#SPILL!",
    "#CALC!",
    "#GETTING_DATA",
]);
export const workbookIssueSchema = z
    .object({
    code: workbookIssueCodeSchema,
    severity: workbookIssueSeveritySchema,
    sheet: z.string().max(100).optional(),
    /** 1-based worksheet row, as the person sees it in Excel. */
    row: z.number().int().positive().optional(),
    /** 0-based column index in the sheet. */
    column: z.number().int().nonnegative().optional(),
    /** Excel column letters of `column`, for example "AP". */
    columnLabel: z.string().max(4).optional(),
    field: lotFieldSchema.optional(),
    /** Header text of the column, when known. */
    header: z.string().max(200).optional(),
    /** Machine-readable expectation, for example "8 digits" or "UF code". */
    expected: z.string().max(200).optional(),
    /** The value found, truncated. */
    found: z.string().max(200).optional(),
    /** How many cells or rows share this finding (rows are listed once, then counted). */
    count: z.number().int().positive().optional(),
    /** The cached Excel error when `code` is CACHED_ERROR_VALUE. */
    cellError: workbookCellErrorSchema.optional(),
})
    .strict();
/* -- Inspection -------------------------------------------------------- */
export const workbookSheetKindSchema = z.enum([
    /** A product list priced by the BackChannel calculator (BC SP, BC ES, BASE…). */
    "SCENARIO",
    /** A commercial simulation for one client; has no NCM, so it cannot be calculated. */
    "CLIENT_SIMULATION",
    /** Premises and lookup tables (Parâmetros Compra / Venda). */
    "PARAMETERS",
    /** Audit, review, summary and client-code sheets. */
    "AUXILIARY",
    "UNKNOWN",
]);
export const workbookMatchMethodSchema = z.enum(["EXACT", "SYNONYM", "FUZZY"]);
export const workbookColumnMatchSchema = z
    .object({
    field: lotFieldSchema,
    column: z.number().int().nonnegative(),
    header: z.string().max(200),
    /** 0..1. Below 0.6 the console asks the person to confirm the column. */
    confidence: z.number().min(0).max(1),
    method: workbookMatchMethodSchema,
    /** Other columns that could feed the same field, best first. */
    alternatives: z.array(z.number().int().nonnegative()).max(5),
})
    .strict();
export const workbookRouteHintSchema = z
    .object({
    /** FIXED when one origin → destination covers the sheet, DYNAMIC when rows differ. */
    suggestedMode: z.enum(["FIXED", "DYNAMIC"]),
    originUf: ufSchema.optional(),
    destinationUf: ufSchema.optional(),
    source: z.enum(["SHEET_NAME", "COLUMN", "HEADER_BLOCK", "PARAMETER_SHEET", "NONE"]),
    /** Distinct routes read from the UF columns, most frequent first. */
    routes: z
        .array(z
        .object({
        originUf: ufSchema.optional(),
        destinationUf: ufSchema.optional(),
        rows: z.number().int().positive(),
    })
        .strict())
        .max(30),
})
    .strict();
export const workbookParameterSchema = z
    .object({
    label: z.string().max(200),
    value: z.string().max(200),
    /** A1 reference of the value cell. */
    cell: z.string().max(20),
    /** Sheet the value was read from. */
    sheet: z.string().max(100),
})
    .strict();
/**
 * The sale scenario a sheet states in its own cells, read so the calculator can offer it as
 * prefilled lot inputs (never applied silently): the TR of "Valor de venda com TR", the UF the
 * sale goes to, and the PIS/COFINS regime of the workbook's premises. Each carries its cell.
 */
export const workbookSaleScenarioSchema = z
    .object({
    /** TR as a fraction (0.052 for 5,2%), from a "TR %" cell above the header. */
    trRate: workbookParameterSchema.optional(),
    /** UF of the sale, from a UF cell above a sale column ("NF venda", "Valor de venda"). */
    saleDestinationUf: workbookParameterSchema.optional(),
    /** NON_CUMULATIVE or CUMULATIVE, from a premise such as "PIS não cumulativo". */
    pisCofinsRegime: workbookParameterSchema.optional(),
    /**
     * Commercial discount of the sale side, as a fraction ("0.1"). Display only: never a
     * calculation input. Header cell (e.g. BM4) is `cell`.
     */
    saleDiscount: workbookParameterSchema.optional(),
    /**
     * Commercial discount of the purchase side, as a fraction ("0.1"). Display only: never a
     * calculation input. Header cell is `cell`.
     */
    purchaseDiscount: workbookParameterSchema.optional(),
})
    .strict();
export const workbookSheetAnalysisSchema = z
    .object({
    kind: workbookSheetKindSchema,
    /** 0..1 */
    kindConfidence: z.number().min(0).max(1),
    hidden: z.boolean(),
    convertible: z.boolean(),
    /** Plain-language reason codes behind `kind`, for display. */
    signals: z.array(z.string().max(80)).max(10),
    headerRow: z.number().int().positive(),
    dataRowCount: z.number().int().nonnegative(),
    columns: z.array(workbookColumnMatchSchema).max(60),
    route: workbookRouteHintSchema,
    /** Scenario premises found above the header (target margin, take rate, destination UF…). */
    parameters: z.array(workbookParameterSchema).max(40),
    /** The sale scenario the sheet's own cells state (TR, sale UF, PIS/COFINS regime). */
    saleScenario: workbookSaleScenarioSchema.optional(),
    /**
     * The decimal separator the sheet's amounts are written with, read from the amounts themselves
     * ("1.234,56" is "," and "1234.56" is "."). Absent when the amounts do not say (whole numbers,
     * "1.500" that reads either way, or a mix), in which case the pt-BR comma is the default.
     */
    decimalSeparator: z.enum([",", "."]).optional(),
    issues: z.array(workbookIssueSchema).max(60),
    cachedErrorCount: z.number().int().nonnegative(),
})
    .strict();
export const lotInspectionSchema = z
    .object({
    templateProfile: z.literal("JAMERSON_V1").optional(),
    sheets: z
        .array(z
        .object({
        name: z.string(),
        rowCount: z.number().int(),
        columnCount: z.number().int(),
        formulaCount: z.number().int(),
        preview: z.array(z.array(z.string())),
        suggestedHeaderRow: z.number().int(),
        suggestedColumns: z.partialRecord(lotFieldSchema, z.number().int()),
        analysis: workbookSheetAnalysisSchema.optional(),
    })
        .strict())
        .max(LOT_MAX_SHEETS),
    /** Findings about the file as a whole (for example a hidden sheet with cached errors). */
    issues: z.array(workbookIssueSchema).max(60).optional(),
    /** Premises read from parameter sheets (Parâmetros Compra / Venda), by sheet. */
    parameterSheets: z
        .array(z
        .object({
        sheet: z.string().max(100),
        parameters: z.array(workbookParameterSchema).max(60),
    })
        .strict())
        .max(10)
        .optional(),
})
    .strict();
/* -- Coverage ---------------------------------------------------------- */
/**
 * NEEDS_REVIEW: a rule is in force for the route, but it states something the
 * route contradicts (an interstate ICMS rate that Res. SF 22/89 does not set
 * for the item's real origin). It is reported, never counted as covered.
 */
export const calculationCoverageStatusSchema = z.enum([
    "COVERED",
    "MISSING",
    "STALE",
    "NEEDS_REVIEW",
]);
/**
 * Why a route is covered, missing or stale. Stable codes: the research worker
 * and the console both branch on them.
 */
export const calculationCoverageReasonSchema = z.enum([
    /** A published rule states this NCM on this route and is effective on the date. */
    "RULE_FOUND",
    /** The ruleset has rules for the NCM, but none for this origin → destination. */
    "NO_RULE_FOR_ROUTE",
    /** The ruleset has no rule for this NCM at all. */
    "NO_RULE_FOR_NCM",
    /** The rule for this NCM and route ended before the calculation date. */
    "RULE_EXPIRED",
    /** The rule for this NCM and route starts after the calculation date. */
    "RULE_NOT_YET_EFFECTIVE",
    /** More than one rule fits and the file has no CEST to choose between them. */
    "AMBIGUOUS_RULES",
    /** The ruleset has no fiscal rules at all. */
    "RULESET_EMPTY",
    /** The rule in force states an interstate ICMS rate other than the one Res. SF 22/89 sets for this route. */
    "INTERSTATE_RATE_MISMATCH",
    /**
     * Only an IPI (TIPI) rule is in force: it states no ICMS for this route, so
     * the route is not covered. Reported MISSING and researchable.
     */
    "ICMS_NOT_PUBLISHED",
    /**
     * No published rule states the route, and the "regra geral (sem ST)" answers it: the statutory
     * interstate rate (or the verified internal rate), no ST proven by a fact or by the national CEST
     * catalog. COVERED, labeled, never a silent default.
     */
    "GENERAL_RULE_NO_ST",
    /**
     * No published rule states this origin, and the destination's published ICMS-ST rule for the NCM
     * and CEST answers it with the statutory interstate rate of the route (engine 1.9.0, "derivada da
     * regra de destino"). COVERED, labeled, only where the destination charges ST on entries from any
     * origin.
     */
    "DESTINATION_ST_DERIVED",
]);
export const calculationCoverageEntrySchema = z
    .object({
    originUf: ufSchema,
    destinationUf: ufSchema,
    ncm: z.string().regex(/^\d{8}$/),
    cest: z
        .string()
        .regex(/^\d{2}\.\d{3}\.\d{2}$/)
        .optional(),
    /**
     * SALE: the sale route of a priced row (center → customer), needed when the row prices the sale
     * (NET_TARGET_MARGIN, COST_PLUS_TR, resale) and entry ST does not close it. Absent: the purchase.
     */
    leg: z.enum(["PURCHASE", "SALE"]).optional(),
    status: calculationCoverageStatusSchema,
    reason: calculationCoverageReasonSchema,
    /** Worksheet rows (as numbered in the file) that need this combination. */
    rowRefs: z.array(z.number().int().positive()).min(1).max(500),
    /** Rules that fit the NCM; context for AMBIGUOUS_RULES, RULE_EXPIRED and RULE_NOT_YET_EFFECTIVE. */
    candidateRuleIds: z.array(z.string().uuid()).max(20).optional(),
    /** The rule that answers this route (COVERED, NEEDS_REVIEW because of its stated rate, or the IPI-only rule behind ICMS_NOT_PUBLISHED). */
    matchedRuleId: z.string().uuid().optional(),
    /**
     * ANY_ORIGIN: the matched rule states no origin UF, so it answers for every origin
     * (destination only). EXACT: the rule names this origin.
     */
    originScope: z.enum(["EXACT", "ANY_ORIGIN"]).optional(),
    /** ANY_DESTINATION: the matched rule states no destination UF either (any origin and destination). */
    destinationScope: z.enum(["EXACT", "ANY_DESTINATION"]).optional(),
    /**
     * The NCM has rules on this route, but none for the file's CEST: the CESTs those rules state.
     * A CEST from the published rules is a repair the person confirms ("Reparar planilha").
     */
    expectedCests: z
        .array(z.string().regex(/^\d{2}\.\d{3}\.\d{2}$/))
        .max(20)
        .optional(),
    /** GENERAL_RULE_NO_ST: what the regra geral used. */
    generalRule: z
        .object({
        noStBasis: z.enum(["NO_ST_FACT", "OUTSIDE_CEST_CATALOG"]),
        interstateRate: z
            .string()
            .regex(/^(0|[1-9]\d{0,5})(\.\d{1,8})?$/)
            .nullable(),
        destinationInternalRate: z
            .string()
            .regex(/^(0|[1-9]\d{0,5})(\.\d{1,8})?$/)
            .nullable(),
    })
        .strict()
        .optional(),
    /** DESTINATION_ST_DERIVED: the destination rule copied and the statutory interstate rate used. */
    destinationStRule: z
        .object({
        sourceRuleId: z.string().uuid(),
        interstateRate: z.string().regex(/^(0|[1-9]\d{0,5})(\.\d{1,8})?$/),
    })
        .strict()
        .optional(),
    /** INTERSTATE_RATE_MISMATCH: the rate the rule states and the one Res. SF 22/89 sets, as fractions. */
    interstateRate: z
        .object({
        stated: z.string().regex(/^(0|[1-9]\d{0,5})(\.\d{1,8})?$/),
        expected: z.string().regex(/^(0|[1-9]\d{0,5})(\.\d{1,8})?$/),
    })
        .strict()
        .optional(),
})
    .strict();
export const calculationCoverageRequestSchema = z
    .object({
    calculationDate: z.string().date(),
    rulesetSelector: rulesetSelectorSchema,
    rows: z
        .array(z
        .object({
        row: z.number().int().positive(),
        item: calculationItemInputSchema,
    })
        .strict())
        .min(1)
        .max(500),
})
    .strict();
/**
 * Which origin → destination × NCM/CEST combinations a file needs, and whether
 * the published ruleset can calculate each one. A MISSING or STALE row is never
 * calculated with a default; it is reported here and in the results.
 */
export const calculationCoverageReportSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    calculationDate: z.string().date(),
    /** Null when the environment has no published ruleset. */
    rulesetVersion: z.string().min(1).max(80).nullable(),
    summary: z
        .object({
        combinations: z.number().int().nonnegative(),
        covered: z.number().int().nonnegative(),
        missing: z.number().int().nonnegative(),
        stale: z.number().int().nonnegative(),
        /** Combinations with a rule in force that needs review; 0 for reports that predate it. */
        needsReview: z.number().int().nonnegative().default(0),
        rows: z.number().int().nonnegative(),
        /**
         * Rows by the worst status of their legs (MISSING, STALE, NEEDS_REVIEW, COVERED): a row whose
         * purchase is covered but whose sale route is missing counts as missing, as the calculation
         * sends it to review. With purchase-only entries this is the sum of each status's rowRefs.
         */
        rowsCovered: z.number().int().nonnegative(),
        rowsMissing: z.number().int().nonnegative(),
        rowsStale: z.number().int().nonnegative(),
        rowsNeedsReview: z.number().int().nonnegative().default(0),
        /** Rows the file could not describe (no NCM or UF); fixed in the sheet, not researched. */
        rowsUnidentified: z.number().int().nonnegative(),
        /** Of the covered rows, those the regra geral (sem ST) answers; absent when none. */
        rowsGeneralRule: z.number().int().nonnegative().optional(),
        /** Of the covered rows, those a rule derived from the destination's ST rule answers. */
        rowsDestinationStRule: z.number().int().nonnegative().optional(),
        /** Rows whose sale route is missing or stale (also counted in rowsMissing / rowsStale). */
        rowsSaleLegMissing: z.number().int().nonnegative().optional(),
    })
        .strict(),
    entries: z.array(calculationCoverageEntrySchema).max(1000),
})
    .strict();
//# sourceMappingURL=workbook.js.map